/**
 * Three.js Light Node Definitions
 *
 * Declaration nodes: hold light params and provide buildLight() for
 * SceneNode to call. On param change, rebuild light in place.
 */

import { BaseNode } from '@nodes/BaseNode';
import { internals, params } from '@/nodes/TypedEndpoints';
import type { NodeDef } from '@runtime/IRuntime';
import type { ThreeDependencies } from '../threejsPlugin';
import type { Three } from '../types';
import { rgbaToHex, DEFAULT_COLORS, type RGBA } from '../colorUtils';

/**
 * Base class for light nodes — provides buildLight() for SceneNode.
 */
abstract class LightNode<T extends Record<string, any>> extends BaseNode<T> {
  protected three: Three;
  /** Stored light reference from SceneNode — for in-place mutation */
  protected storedLight: any = null;

  constructor(id: string, deps: ThreeDependencies) {
    super(id, deps);
    this.three = deps.three;
  }

  /** Build light from current params — called by SceneNode */
  buildLight(): any {
    return this.createLight();
  }

  protected abstract createLight(): any;

  /** Store light reference from SceneNode for in-place mutation */
  setThreeObject(light: any): void {
    this.storedLight = light;
  }

  /** Per-frame: re-evaluate expression params, apply all params to stored light */
  animate(): void {
    if (!this.storedLight) return;
    for (const id of this.deps.endpointApi.getEndpointIds()) {
      const meta = this.deps.endpointApi.getMetadata(id);
      if (meta?.dataFlow === 'param') {
        this.onParamChanged(undefined as any, id);
      }
    }
  }

  /** Subclasses override to mutate storedLight directly */
  onParamChanged(_payload: any, _endpointId: string): void {
    // Default: no-op; subclasses implement per-param mutation
  }
}

// ─────────────────────────────────────────────────────────────
// Ambient Light (uniform lighting from all directions)
// ─────────────────────────────────────────────────────────────

const ambientLightEndpoints = {
  ...internals({
    lightOut: { dataType: 'three:light', isOutput: true }
  }),
  ...params({
    color: { dataType: 'color', value: DEFAULT_COLORS.white },
    intensity: { value: 0.5, min: 0, max: 2, step: 0.01 }
  })
};

class AmbientLightNode extends LightNode<typeof ambientLightEndpoints> {
  protected createLight() {
    const light = new this.three.AmbientLight(
      rgbaToHex(this.params.color.value as RGBA),
      this.params.intensity.value
    );
    return light;
  }

  onParamChanged(_payload: any, endpointId: string): void {
    if (!this.storedLight) return;
    if (endpointId === 'color') {
      this.storedLight.color.set(rgbaToHex(this.params.color.value as RGBA));
    } else if (endpointId === 'intensity') {
      this.storedLight.intensity = this.params.intensity.value;
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Directional Light (sunlight-like, parallel rays)
// ─────────────────────────────────────────────────────────────

const directionalLightEndpoints = {
  ...internals({
    lightOut: { dataType: 'three:light', isOutput: true }
  }),
  ...params({
    color: { dataType: 'color', value: DEFAULT_COLORS.white },
    intensity: { value: 1, min: 0, max: 5, step: 0.1 },
    castShadow: { dataType: 'boolean', value: true },
    posX: { value: 5, min: -100, max: 100, step: 0.1, group: 'Position' },
    posY: { value: 10, min: -100, max: 100, step: 0.1, group: 'Position' },
    posZ: { value: 5, min: -100, max: 100, step: 0.1, group: 'Position' },
    shadowSize: { value: 10, min: 1, max: 200, step: 1, label: 'Shadow Size', group: 'Shadow' },
    shadowNear: { value: 0.5, min: 0.1, max: 50, step: 0.1, label: 'Near', group: 'Shadow' },
    shadowFar: { value: 50, min: 1, max: 500, step: 1, label: 'Far', group: 'Shadow' }
  })
};

class DirectionalLightNode extends LightNode<typeof directionalLightEndpoints> {
  protected createLight() {
    const light = new this.three.DirectionalLight(
      rgbaToHex(this.params.color.value as RGBA),
      this.params.intensity.value
    );
    light.position.set(
      this.params.posX.value,
      this.params.posY.value,
      this.params.posZ.value
    );
    light.castShadow = this.params.castShadow.value;
    light.shadow.mapSize.width = 1024;
    light.shadow.mapSize.height = 1024;
    const size = this.params.shadowSize.value;
    light.shadow.camera.near = this.params.shadowNear.value;
    light.shadow.camera.far = this.params.shadowFar.value;
    light.shadow.camera.left = -size;
    light.shadow.camera.right = size;
    light.shadow.camera.top = size;
    light.shadow.camera.bottom = -size;
    return light;
  }

  onParamChanged(_payload: any, endpointId: string): void {
    if (!this.storedLight) return;
    if (endpointId === 'color') {
      this.storedLight.color.set(rgbaToHex(this.params.color.value as RGBA));
    } else if (endpointId === 'intensity') {
      this.storedLight.intensity = this.params.intensity.value;
    } else if (endpointId === 'castShadow') {
      this.storedLight.castShadow = this.params.castShadow.value;
    } else if (endpointId === 'posX' || endpointId === 'posY' || endpointId === 'posZ') {
      this.storedLight.position.set(
        this.params.posX.value, this.params.posY.value, this.params.posZ.value
      );
    } else if (endpointId === 'shadowSize' || endpointId === 'shadowNear' || endpointId === 'shadowFar') {
      const size = this.params.shadowSize.value;
      this.storedLight.shadow.camera.near = this.params.shadowNear.value;
      this.storedLight.shadow.camera.far = this.params.shadowFar.value;
      this.storedLight.shadow.camera.left = -size;
      this.storedLight.shadow.camera.right = size;
      this.storedLight.shadow.camera.top = size;
      this.storedLight.shadow.camera.bottom = -size;
      this.storedLight.shadow.camera.updateProjectionMatrix();
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Point Light (emits in all directions from a point)
// ─────────────────────────────────────────────────────────────

const pointLightEndpoints = {
  ...internals({
    lightOut: { dataType: 'three:light', isOutput: true }
  }),
  ...params({
    color: { dataType: 'color', value: DEFAULT_COLORS.white },
    intensity: { value: 1, min: 0, max: 5, step: 0.1 },
    distance: { value: 0, min: 0, max: 100, step: 0.1 },
    decay: { value: 2, min: 0, max: 5, step: 0.1 },
    castShadow: { dataType: 'boolean', value: false },
    posX: { value: 0, min: -100, max: 100, step: 0.1, group: 'Position' },
    posY: { value: 5, min: -100, max: 100, step: 0.1, group: 'Position' },
    posZ: { value: 0, min: -100, max: 100, step: 0.1, group: 'Position' }
  })
};

class PointLightNode extends LightNode<typeof pointLightEndpoints> {
  protected createLight() {
    const light = new this.three.PointLight(
      rgbaToHex(this.params.color.value as RGBA),
      this.params.intensity.value,
      this.params.distance.value,
      this.params.decay.value
    );
    light.position.set(
      this.params.posX.value,
      this.params.posY.value,
      this.params.posZ.value
    );
    light.castShadow = this.params.castShadow.value;
    light.shadow.mapSize.width = 512;
    light.shadow.mapSize.height = 512;
    return light;
  }

  onParamChanged(_payload: any, endpointId: string): void {
    if (!this.storedLight) return;
    if (endpointId === 'color') {
      this.storedLight.color.set(rgbaToHex(this.params.color.value as RGBA));
    } else if (endpointId === 'intensity') {
      this.storedLight.intensity = this.params.intensity.value;
    } else if (endpointId === 'distance') {
      this.storedLight.distance = this.params.distance.value;
    } else if (endpointId === 'decay') {
      this.storedLight.decay = this.params.decay.value;
    } else if (endpointId === 'castShadow') {
      this.storedLight.castShadow = this.params.castShadow.value;
    } else if (endpointId === 'posX' || endpointId === 'posY' || endpointId === 'posZ') {
      this.storedLight.position.set(
        this.params.posX.value, this.params.posY.value, this.params.posZ.value
      );
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Spot Light (cone of light from a point)
// ─────────────────────────────────────────────────────────────

const spotLightEndpoints = {
  ...internals({
    lightOut: { dataType: 'three:light', isOutput: true }
  }),
  ...params({
    color: { dataType: 'color', value: DEFAULT_COLORS.white },
    intensity: { value: 1, min: 0, max: 5, step: 0.1 },
    distance: { value: 0, min: 0, max: 100, step: 0.1 },
    angle: { value: 0.5, min: 0, max: 1.5707, step: 0.01 },
    penumbra: { value: 0.5, min: 0, max: 1, step: 0.01 },
    decay: { value: 2, min: 0, max: 5, step: 0.1 },
    castShadow: { dataType: 'boolean', value: false },
    posX: { value: 0, min: -100, max: 100, step: 0.1, group: 'Position' },
    posY: { value: 10, min: -100, max: 100, step: 0.1, group: 'Position' },
    posZ: { value: 0, min: -100, max: 100, step: 0.1, group: 'Position' },
    targetX: { value: 0, min: -100, max: 100, step: 0.1, group: 'Target' },
    targetY: { value: 0, min: -100, max: 100, step: 0.1, group: 'Target' },
    targetZ: { value: 0, min: -100, max: 100, step: 0.1, group: 'Target' }
  })
};

class SpotLightNode extends LightNode<typeof spotLightEndpoints> {
  protected createLight() {
    const light = new this.three.SpotLight(
      rgbaToHex(this.params.color.value as RGBA),
      this.params.intensity.value,
      this.params.distance.value,
      this.params.angle.value,
      this.params.penumbra.value,
      this.params.decay.value
    );
    light.position.set(
      this.params.posX.value,
      this.params.posY.value,
      this.params.posZ.value
    );
    light.target.position.set(
      this.params.targetX.value,
      this.params.targetY.value,
      this.params.targetZ.value
    );
    light.castShadow = this.params.castShadow.value;
    light.shadow.mapSize.width = 1024;
    light.shadow.mapSize.height = 1024;
    return light;
  }

  onParamChanged(_payload: any, endpointId: string): void {
    if (!this.storedLight) return;
    if (endpointId === 'color') {
      this.storedLight.color.set(rgbaToHex(this.params.color.value as RGBA));
    } else if (endpointId === 'intensity') {
      this.storedLight.intensity = this.params.intensity.value;
    } else if (endpointId === 'distance') {
      this.storedLight.distance = this.params.distance.value;
    } else if (endpointId === 'angle') {
      this.storedLight.angle = this.params.angle.value;
    } else if (endpointId === 'penumbra') {
      this.storedLight.penumbra = this.params.penumbra.value;
    } else if (endpointId === 'decay') {
      this.storedLight.decay = this.params.decay.value;
    } else if (endpointId === 'castShadow') {
      this.storedLight.castShadow = this.params.castShadow.value;
    } else if (endpointId === 'posX' || endpointId === 'posY' || endpointId === 'posZ') {
      this.storedLight.position.set(
        this.params.posX.value, this.params.posY.value, this.params.posZ.value
      );
    } else if (endpointId === 'targetX' || endpointId === 'targetY' || endpointId === 'targetZ') {
      this.storedLight.target.position.set(
        this.params.targetX.value, this.params.targetY.value, this.params.targetZ.value
      );
    }
  }
}

// ─────────────────────────────────────────────────────────────
// Hemisphere Light (sky/ground gradient lighting)
// ─────────────────────────────────────────────────────────────

const hemisphereLightEndpoints = {
  ...internals({
    lightOut: { dataType: 'three:light', isOutput: true }
  }),
  ...params({
    skyColor: { dataType: 'color', value: DEFAULT_COLORS.white, label: 'Sky' },
    groundColor: { dataType: 'color', value: { r: 0.2, g: 0.1, b: 0.05, a: 1 }, label: 'Ground' },
    intensity: { value: 1, min: 0, max: 2, step: 0.01 },
    posY: { value: 10, min: -100, max: 100, step: 0.1, label: 'Height' }
  })
};

class HemisphereLightNode extends LightNode<typeof hemisphereLightEndpoints> {
  protected createLight() {
    const light = new this.three.HemisphereLight(
      rgbaToHex(this.params.skyColor.value as RGBA),
      rgbaToHex(this.params.groundColor.value as RGBA),
      this.params.intensity.value
    );
    light.position.set(0, this.params.posY.value, 0);
    return light;
  }

  onParamChanged(_payload: any, endpointId: string): void {
    if (!this.storedLight) return;
    if (endpointId === 'skyColor') {
      this.storedLight.color.set(rgbaToHex(this.params.skyColor.value as RGBA));
    } else if (endpointId === 'groundColor') {
      this.storedLight.groundColor.set(rgbaToHex(this.params.groundColor.value as RGBA));
    } else if (endpointId === 'intensity') {
      this.storedLight.intensity = this.params.intensity.value;
    } else if (endpointId === 'posY') {
      this.storedLight.position.set(0, this.params.posY.value, 0);
    }
  }
}

export const lightNodes: NodeDef[] = [
  {
    type: 'AmbientLight',
    shortName: 'ambient',
    category: 'light',
    endpoints: ambientLightEndpoints,
    ctor: AmbientLightNode
  },
  {
    type: 'DirectionalLight',
    shortName: 'directional',
    category: 'light',
    endpoints: directionalLightEndpoints,
    ctor: DirectionalLightNode
  },
  {
    type: 'PointLight',
    shortName: 'point',
    category: 'light',
    endpoints: pointLightEndpoints,
    ctor: PointLightNode
  },
  {
    type: 'SpotLight',
    shortName: 'spot',
    category: 'light',
    endpoints: spotLightEndpoints,
    ctor: SpotLightNode
  },
  {
    type: 'HemisphereLight',
    shortName: 'hemi',
    category: 'light',
    endpoints: hemisphereLightEndpoints,
    ctor: HemisphereLightNode
  }
];
